/**
 * Search params của màn danh sách sản phẩm.
 *
 * Filter trạng thái mặc định là "đang hoạt động": vào `/products` là thấy ngay
 * hàng đang bán, không phải lọc tay. Vì vậy `active` bỏ trống KHÔNG còn nghĩa là
 * "tất cả" — muốn xem toàn bộ phải chọn tường minh `active=all`.
 *
 * Config bộ lọc để ở đây (thay vì khai inline trong file route như các màn
 * khác) để giữ được bộ test đơn vị vốn đã có cho phần parse URL.
 */
import { defineListFilters } from '@/components/common/filters/core'
import { remoteSelectFilter } from '@/components/common/filters/remote-select-filter'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useProductCategories } from '@/hooks/use-product-category-query'
import { m } from '@/lib/paraglide/messages'

export type ProductActiveFilter = 'all' | 'active' | 'inactive'

export const DEFAULT_PRODUCT_ACTIVE_FILTER: ProductActiveFilter = 'active'

/**
 * Type alias chứ không phải `interface`: interface không có index signature
 * ngầm nên TanStack từ chối khi truyền vào prop `search` của `<Link to="/products">`.
 */
export type ProductsSearch = {
    q?: string
    active?: ProductActiveFilter
    sellable?: 'sellable' | 'internal'
    /** MÃ danh mục sản phẩm (TUI_12_5G...); 'all' = không lọc. */
    category?: string
    page?: number
}

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * `active` có mặc định KHÁC RỖNG nên `active=active` không được ghi vào URL và
 * cũng không tính là "đang lọc" — giống màn Đối soát.
 */
export const PRODUCTS_FILTERS = defineListFilters({
    q: textFilter({
        label: m.product_search_label,
        placeholder: m.product_search_placeholder,
    }),
    active: selectFilter<ProductActiveFilter>({
        label: m.product_status_filter_label,
        defaultValue: DEFAULT_PRODUCT_ACTIVE_FILTER,
        options: () => [
            { value: 'all', label: m.vehicle_status_all() },
            { value: 'active', label: m.product_active() },
            { value: 'inactive', label: m.product_inactive_label() },
        ],
    }),
    // Danh mục lấy từ server nên phải dùng remoteSelectFilter: tập giá trị hợp lệ
    // chỉ biết lúc chạy, người dùng thêm danh mục mới bất cứ lúc nào.
    category: remoteSelectFilter({
        label: m.product_category_field_label,
        allLabel: m.product_category_filter_all,
        useOptions: () => {
            const { data } = useProductCategories({ is_active: true })
            return (data?.data ?? []).map((c) => ({
                value: c.code,
                label: c.name,
            }))
        },
    }),
    sellable: selectFilter<'all' | 'sellable' | 'internal'>({
        label: m.product_sellable_filter_label,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.product_filter_all_sellable() },
            { value: 'sellable', label: m.product_filter_sellable() },
            { value: 'internal', label: m.product_filter_internal() },
        ],
    }),
})

/** `undefined` = không lọc theo trạng thái. */
export function toIsActiveParam(
    filter: ProductActiveFilter,
): boolean | undefined {
    if (filter === 'all') return undefined
    return filter === 'active'
}
